import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with institute context support
const instituteApi = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and institute context
instituteApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const instituteSlug = localStorage.getItem('instituteSlug');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (instituteSlug) {
      config.headers['X-Institute-Slug'] = instituteSlug;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
instituteApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Institute API Service
export const instituteService = {
  // ==================== PUBLIC ====================
  
  // Resolve institute by slug/subdomain/domain
  resolve: (identifier, type = 'slug') => 
    instituteApi.get(`/institutes/resolve/${identifier}?type=${type}`),
  
  // Public institute self-registration
  register: (registrationData) => 
    instituteApi.post('/institutes/register', registrationData),
  
  // ==================== INSTITUTE ADMIN ====================
  
  // Get own institute details
  getMyInstitute: () => instituteApi.get('/institutes/my-institute'),

  // Get effective access (permissions + modules) for current user in tenant context
  getMyInstituteAccess: () => instituteApi.get('/institutes/my-institute/access'),
  
  // Update own institute
  updateMyInstitute: (data) => instituteApi.put('/institutes/my-institute', data),
  
  // Get branding settings
  getMyBranding: () => instituteApi.get('/institutes/my-institute/branding'),
  
  // Update branding settings
  updateMyBranding: (branding) => instituteApi.put('/institutes/my-institute/branding', branding),

  // Upload institute-admin branding asset (logo/favicon/background) to S3
  uploadMyBrandingAsset: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return instituteApi.post('/institutes/my-institute/branding/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Get usage limits
  getMyLimits: () => instituteApi.get('/institutes/my-institute/limits'),

  // ==================== INSTITUTE USERS ====================

  // List users within current institute
  listInstituteUsers: (params = {}) => instituteApi.get('/institute/users', { params }),

  // Create user within current institute (use role='student' for add-student flow)
  createInstituteUser: (data) => instituteApi.post('/institute/users', data),

  // Delete user from institute (soft delete)
  deleteInstituteUser: (userId) => instituteApi.delete(`/institute/users/${userId}`),

  // Update user in institute (for suspend/activate)
  updateInstituteUser: (userId, data) => instituteApi.put(`/institute/users/${userId}`, data),

  // Institute classes CRUD
  listInstituteClasses: (params = {}) => instituteApi.get('/institute/users/classes', { params }),
  createInstituteClass: (data) => instituteApi.post('/institute/users/classes', data),
  updateInstituteClass: (classId, data) => instituteApi.put(`/institute/users/classes/${classId}`, data),
  deleteInstituteClass: (classId) => instituteApi.delete(`/institute/users/classes/${classId}`),

  // Institute batches CRUD (using new runway-based API)
  createInstituteBatch: (data) => instituteApi.post('/institute/users/batches', data),
  updateInstituteBatch: (batchId, data) => instituteApi.put(`/institute/users/batches/${batchId}`, data),
  deleteInstituteBatch: (batchId) => instituteApi.delete(`/institute/users/batches/${batchId}`),
  listInstituteBatches: (params = {}) => instituteApi.get('/institute/users/batches', { params }),

  // Runways CRUD
  listRunways: (params = {}) => instituteApi.get('/batches/runways/options', { params }),
  listBatchesByRunway: (runwayId, params = {}) => {
    console.log('🔍 DEBUG: API call listBatchesByRunway with runwayId:', runwayId);
    return instituteApi.get(`/batches/by-runway/${runwayId}`, { params });
  },
  
  // ==================== USER ====================

  // Get batches for current user's institute
  getUserInstituteBatches: (params = {}) => instituteApi.get('/user/batches', { params }),

  // ==================== SUPER ADMIN - INSTITUTES ====================
  
  // List all institutes
  listInstitutes: (page = 1, limit = 20, status = '', search = '') => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return instituteApi.get(`/superadmin/institutes?${params}`);
  },
  
  // Create new institute
  createInstitute: (instituteData) => 
    instituteApi.post('/superadmin/institutes', instituteData),

  // Upload branding asset (logo/favicon) to S3
  uploadBrandingAsset: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return instituteApi.post('/superadmin/institutes/branding/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Get institute details
  getInstitute: (id) => instituteApi.get(`/superadmin/institutes/${id}`),

  // Create institute admin (superadmin)
  createInstituteAdmin: (id, data) => instituteApi.post(`/superadmin/institutes/${id}/admins`, data),

  // Update institute admin (superadmin)
  updateInstituteAdmin: (id, adminUserId, data) => instituteApi.put(`/superadmin/institutes/${id}/admins/${adminUserId}`, data),

  // Reset institute admin password (superadmin)
  resetInstituteAdminPassword: (id, adminUserId) =>
    instituteApi.post(`/superadmin/institutes/${id}/admins/${adminUserId}/reset-password`),
  
  // Update institute
  updateInstitute: (id, data) => instituteApi.put(`/superadmin/institutes/${id}`, data),

  // Update institute branding (superadmin)
  updateInstituteBranding: (id, data) => instituteApi.put(`/superadmin/institutes/${id}/branding`, data),
  
  // Delete institute (soft delete)
  deleteInstitute: (id) => instituteApi.delete(`/superadmin/institutes/${id}`),

  // Get delete preview stats
  getInstituteDeletePreview: (id) => instituteApi.get(`/superadmin/institutes/${id}/delete-preview`),

  // Hard delete institute with related data
  hardDeleteInstitute: (id) => instituteApi.delete(`/superadmin/institutes/${id}/hard-delete`),
  
  // Activate institute
  activateInstitute: (id) => instituteApi.post(`/superadmin/institutes/${id}/activate`),
  
  // Suspend institute
  suspendInstitute: (id) => instituteApi.post(`/superadmin/institutes/${id}/suspend`),
  
  // ==================== SUPER ADMIN - REGISTRATION REQUESTS ====================
  
  // List pending registration requests
  getPendingRequests: (page = 1, limit = 20) => 
    instituteApi.get(`/superadmin/institutes/requests/pending?page=${page}&limit=${limit}`),
  
  // Approve registration request
  approveRequest: (requestId) => 
    instituteApi.post(`/superadmin/institutes/requests/${requestId}/approve`),
  
  // Reject registration request
  rejectRequest: (requestId, reason = '') => 
    instituteApi.post(`/superadmin/institutes/requests/${requestId}/reject?reason=${encodeURIComponent(reason)}`),
  
  // ==================== SUPER ADMIN - CONTENT SHARING ====================
  
  // Share content with institutes
  shareContent: (contentType, contentId, instituteIds, accessType = 'read') => 
    instituteApi.post('/superadmin/institutes/content/share', {
      content_type: contentType,
      content_id: contentId,
      institute_ids: instituteIds,
      access_type: accessType
    }),
  
  // Update content visibility
  updateContentVisibility: (contentType, contentId, visibility) => 
    instituteApi.put('/superadmin/institutes/content/visibility', {
      content_type: contentType,
      content_id: contentId,
      visibility
    }),
  
  // Get content access list
  getContentAccess: (contentType, contentId) => 
    instituteApi.get(`/superadmin/institutes/content/${contentType}/${contentId}/access`),
  
  // ==================== SUPER ADMIN - SUBSCRIPTION PLANS ====================
  
  // List subscription plans
  getPlans: () => instituteApi.get('/superadmin/institutes/plans'),
  
  // Create subscription plan
  createPlan: (data) => instituteApi.post('/superadmin/institutes/plans', data),
  
  // Update subscription plan
  updatePlan: (planId, data) => instituteApi.put(`/superadmin/institutes/plans/${planId}`, data),

  // Get institute RBAC/module controls
  getInstituteAccessControl: (id) => instituteApi.get(`/superadmin/institutes/${id}/access-control`),

  // Update institute-wide permission toggles
  updateInstitutePermissions: (id, permissions) =>
    instituteApi.put(`/superadmin/institutes/${id}/permissions`, { permissions }),

  // Update per-user permission overrides for an institute user
  updateInstituteUserPermissions: (id, userId, overrides) =>
    instituteApi.put(`/superadmin/institutes/${id}/users/${userId}/permissions`, { overrides }),

  // ==================== SUBSCRIPTION RENEWAL ====================
  
  // Renew institute subscription
  renewInstituteSubscription: (id, data) =>
    instituteApi.post(`/institutes/${id}/renew-subscription`, data),
};

export default instituteApi;
export { instituteApi };
